<template>
  <el-dialog
    v-model="localVisible"
    @close="onClose"
    :close-on-press-escape="false"
    append-to-body
    destroy-on-close
    class="appointment-modal"
    width="760px"
  >
    <div class="appointment-modal-body">
      <div class="appointment-header-visual">
        <div class="header-row">
          <div class="header-item">
            <el-icon><Calendar /></el-icon>
            <span>{{ form.date }}</span>
            <span class="header-muted">
              ({{ formattedDate }}
              <span class="date-weekday" :class="{ weekend: isWeekend }">{{ weekdayLabel }}</span>)
            </span>
          </div>
          <div class="header-item">
            <el-icon><Clock /></el-icon>
            <span>{{ form.time }}</span>
          </div>
        </div>
        <div class="header-row">
          <div class="header-item">
            <el-icon><UserFilled /></el-icon>
            <span>{{ form.employee.user_name }}</span>
          </div>
          <div class="header-item">
            <el-icon><OfficeBuilding /></el-icon>
            <span>{{ form.employee.room_name }}</span>
          </div>
        </div>
        <div class="header-row status-row">
          <div class="header-item">
            <el-icon><Clock /></el-icon>
            <span>{{ form.duration || totalDuration }} мин</span>
          </div>
        </div>
      </div>

      <el-form :model="form" label-position="top" class="appointment-form">
        <el-form-item label="Клиент">
          <el-autocomplete
            v-model="clientSearch"
            :fetch-suggestions="searchClientsFn"
            placeholder="Телефон или имя"
            @select="selectClient"
            @change="onClientInput"
            :trigger-on-focus="false"
            style="width: 100%;"
          >
            <template #default="{ item }">
              <div v-if="item.create" class="autocomplete-create">
                <el-icon><Plus /></el-icon>
                <span>Создать клиента: <b>{{ item.value }}</b></span>
              </div>
              <div v-else class="autocomplete-client-item">
                <div class="client-row">
                  <el-icon><UserFilled /></el-icon>
                  <span>{{ item.fio }}</span>
                </div>
                <div class="client-row" v-if="item.phone">
                  <el-icon><Phone /></el-icon>
                  <span>{{ item.phone }}</span>
                </div>
              </div>
            </template>
          </el-autocomplete>

          <div class="client-selected-block" v-if="form.client">
            <div class="client-row">
              <el-icon><Phone /></el-icon>
              <span>{{ form.client.phone }}</span>
            </div>
            <div class="client-row">
              <el-icon><UserFilled /></el-icon>
              <span>{{ form.client.fio }}</span>
            </div>
            <el-link @click="form.client = null" type="danger" class="client-selected-reset">Сбросить</el-link>
          </div>
        </el-form-item>

        <el-form-item label="Услуги">
          <ServiceMultiSelect
            v-model="form.services"
            :services="services"
            :remote="true"
            :city-id="form.employee?.city_id"
          />

          <div class="services-table" v-if="form.services.length">
            <div class="services-head">
              <span>Услуга</span>
              <span>Минуты</span>
              <span>Сумма</span>
            </div>
            <div class="services-row" v-for="srv in form.services" :key="srv.id || srv.name">
              <span>{{ srv.name }}</span>
              <span>{{ srv.duration || 0 }}</span>
              <span>{{ srv.price || 0 }}</span>
            </div>
          </div>
          <div class="services-total" v-if="form.services.length">
            Общая сумма: <b>{{ totalPrice }} ₽</b>
          </div>
        </el-form-item>

        <el-form-item label="Длительность">
          <div class="duration-control">
            <el-button class="duration-btn" @click="changeDuration(-15)">-</el-button>
            <div class="duration-value">{{ form.duration || 0 }} мин.</div>
            <el-button class="duration-btn" @click="changeDuration(15)">+</el-button>
          </div>
        </el-form-item>

        <el-form-item label="Комментарий">
          <el-input
            type="textarea"
            v-model="form.comment"
            placeholder="Комментарий"
          />
        </el-form-item>
      </el-form>
    </div>

    <template #footer>
      <div class="modal-footer-actions">
        <el-button @click="onClose">Отмена</el-button>
        <el-button type="primary" @click="save" :disabled="!canSave">Записать</el-button>
      </div>
    </template>

    <QuickClientCreate
      :visible="showQuickCreate"
      :city-id="form.employee.city_id"
      :default-fio="quickCreateForm.fio"
      :default-phone="quickCreateForm.phone"
      @save="handleQuickCreateClient"
      @close="showQuickCreate = false"
    />
  </el-dialog>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import QuickClientCreate from '@/components/util/calendar/QuickClientCreate.vue'
import { Calendar, Clock, OfficeBuilding, Phone, Plus, UserFilled } from '@element-plus/icons-vue'
import ServiceMultiSelect from '@/components/util/calendar/ServiceMultiSelect.vue'

export default {
  name: 'AppointmentCreateModal',
  components: { Plus, Phone, ServiceMultiSelect, Clock, UserFilled, OfficeBuilding, QuickClientCreate, Calendar },
  props: {
    visible: { type: Boolean, required: true },
    date: { type: String, required: true },
    time: { type: String, required: true, default: '' },
    employee: { type: Object, required: true }
  },
  data() {
    return {
      quickCreateForm: {
        fio: '',
        phone: ''
      },
      localVisible: this.visible,
      form: {
        date: this.date,
        time: this.time,
        employee: this.employee,
        services: [],
        serviceIds: [],
        duration: 0,
        client: null,
        comment: ''
      },
      clientSearch: '',
      showQuickCreate: false,
      lastSuggestions: [],
      showCreateClientButton: false,
      durationEdited: false
    }
  },
  computed: {
    weekdayLabel() {
      if (!this.date) return ''
      const d = new Date(this.date)
      if (isNaN(d.getTime())) return ''
      const weekdays = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
      return weekdays[d.getDay()]
    },
    isWeekend() {
      if (!this.date) return false
      const d = new Date(this.date)
      if (isNaN(d.getTime())) return false
      return d.getDay() === 0 || d.getDay() === 6
    },
    ...mapState('clients', ['search_clients']),
    ...mapState('services', ['services']),
    canSave() {
      return this.form.services.length > 0 && this.form.client
    },
    totalDuration() {
      return this.form.services.reduce((sum, s) => sum + (+s.duration || 0), 0)
    },
    totalPrice() {
      return (this.form.services || []).reduce((sum, s) => sum + (+s.price || 0), 0)
    },
    formattedDate() {
      const months = [
        'Января', 'Февраля', 'Марта', 'Апреля', 'Мая', 'Июня',
        'Июля', 'Августа', 'Сентября', 'Октября', 'Ноября', 'Декабря'
      ]
      const [, month, day] = this.form.date.split('-')
      const monthName = months[parseInt(month, 10) - 1]
      return `${day} ${monthName}`
    }
  },
  watch: {
    visible(val) {
      this.localVisible = val
      if (val) this.resetForm()
    },
    localVisible(val) {
      if (!val) this.$emit('update:visible', false)
    },
    date(val) {
      this.form.date = val
    },
    time(val) {
      this.form.time = val
    },
    employee(val) {
      this.form.employee = val
    },
    'form.services': {
      handler() {
        if (!this.durationEdited) {
          this.form.duration = this.totalDuration
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    ...mapActions('clients', ['searchClients']),
    async searchClientsFn(query, cb) {
      if (!query || query.length < 3) return cb([])

      await this.searchClients({ query })
      let results = (this.search_clients || []).map(c => ({
        ...c,
        value: c.fio + (c.phone ? ` (${c.phone})` : '')
      }))

      this.lastSuggestions = results

      if (results.length === 0) {
        results = [{ value: query, create: true }]
      }

      cb(results)
    },
    onClientInput(val) {
      this.showCreateClientButton = val && val.length >= 3 && this.lastSuggestions.length === 0
    },
    selectClient(item) {
      if (item.create) {
        this.openQuickCreateClient(this.clientSearch)
      } else {
        this.form.client = item
        this.clientSearch = ''
        this.showCreateClientButton = false
      }
    },
    openQuickCreateClient(query) {
      let phone = ''
      let fio = ''
      if (/^\d[\d\-+()\s]+$/.test(query)) phone = query
      else fio = query
      this.quickCreateForm = { fio, phone }
      this.showQuickCreate = true
    },
    async handleQuickCreateClient(payload) {
      const res = await this.$store.dispatch('clients/saveQuickClient', payload)

      const newClient = res && res.id
        ? { id: res.id, fio: payload.fio, phone: payload.phone }
        : { fio: payload.fio, phone: payload.phone }
      this.form.client = newClient
      this.clientSearch = newClient.fio
      this.showQuickCreate = false
      this.showCreateClientButton = false
    },
    resetForm() {
      this.form = {
        date: this.date,
        time: this.time,
        employee: this.employee,
        services: [],
        duration: 0,
        client: null,
        comment: ''
      }
      this.clientSearch = ''
      this.showCreateClientButton = false
      this.lastSuggestions = []
      this.durationEdited = false
    },
    onClose() {
      this.localVisible = false
    },
    changeDuration(step) {
      this.durationEdited = true
      const next = (this.form.duration || 0) + step
      this.form.duration = Math.max(0, next)
    },
    async save() {
      const totalDuration = this.form.duration || (this.form.services || []).reduce((sum, srv) => sum + (+srv.duration || 0), 0)

      function addMinutes(time, min) {
        let [h, m] = time.split(':').map(Number)
        m += min
        h += Math.floor(m / 60)
        m = m % 60
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
      }

      const endTime = addMinutes(this.form.time, totalDuration)

      const appointment = {
        id: this.form.id || null,
        branch_id: this.form.employee.branch_id,
        city_id: this.form.employee.city_id,
        room_id: this.form.employee.room_id,
        user_id: this.form.employee.user_id,
        user_name: this.form.employee.user_name,
        client_id: this.form.client.id,
        client_name: this.form.client.fio,
        client_phone: this.form.client.phone,
        date: this.form.date,
        start_time: this.form.time,
        end_time: endTime,
        services: this.form.services,
        comment: this.form.comment,
        duration_time: totalDuration,
        status: 'future',
        color: 'var(--ui-color-brand-strong)'
      }
      await this.$store.dispatch('appointments/saveAppointment', appointment)
      this.onClose()
    }
  }
}
</script>

<style scoped>
.appointment-modal-body {
  background: linear-gradient(132deg, #264a8f 0%, #10295d 70%);
  border-radius: 12px;
  padding: 16px;
}

.date-weekday {
  font-size: 14px;
  color: #7ab6ff;
  font-weight: 700;
}

.date-weekday.weekend {
  color: #ff8f8f;
}

.header-muted {
  color: #9eb6d9;
}

.appointment-header-visual {
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.header-row {
  display: flex;
  gap: 32px;
  align-items: center;
  justify-content: space-between;
}

.header-item {
  display: flex;
  align-items: center;
  font-size: 16px;
  color: #dce8ff;
  gap: 8px;
}

.status-row {
  margin-top: -4px;
}

.appointment-form :deep(.el-form-item__label) {
  color: #dce8ff;
  font-weight: 700;
}

.appointment-form :deep(.el-input__wrapper),
.appointment-form :deep(.el-textarea__inner),
.appointment-form :deep(.el-select__wrapper) {
  background: #35579a;
  box-shadow: 0 0 0 1px #3e67b3 inset !important;
  color: #eaf3ff;
}

.appointment-form :deep(.el-input__inner),
.appointment-form :deep(.el-textarea__inner) {
  color: #eaf3ff;
}

.appointment-form :deep(.el-textarea__inner::placeholder),
.appointment-form :deep(.el-input__inner::placeholder) {
  color: #8ac1ff;
}

.autocomplete-create {
  display: flex;
  align-items: center;
  color: #52c9ff;
  gap: 8px;
}

.client-selected-block {
  margin-top: 12px;
  background: rgba(10, 26, 63, 0.75);
  border-radius: 8px;
  border: 1px solid #2e4f8c;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  width: 100%;
}

.client-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  color: #dce8ff;
}

.client-selected-reset {
  align-self: flex-end;
}

.services-table {
  margin-top: 10px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(10, 26, 63, 0.75);
}

.services-head,
.services-row {
  display: grid;
  grid-template-columns: 1fr 110px 110px;
}

.services-head span {
  color: #dce8ff;
  font-weight: 700;
  padding: 10px 12px;
}

.services-row span {
  color: #5bc7ff;
  padding: 10px 12px;
  border-top: 1px solid #294675;
}

.services-total {
  margin-top: 10px;
  text-align: right;
  color: #dce8ff;
  font-size: 16px;
}

.services-total b {
  color: #58cbff;
}

.duration-control {
  display: flex;
  align-items: center;
  gap: 8px;
}

.duration-btn {
  width: 42px;
  height: 42px;
  font-size: 24px;
  border-radius: 8px;
  background: #173a78;
  border: none;
  color: #5bc7ff;
}

.duration-value {
  min-width: 120px;
  text-align: center;
  background: #35579a;
  color: #eaf3ff;
  border-radius: 8px;
  font-size: 18px;
  padding: 8px 12px;
}

.modal-footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

:deep(.appointment-modal .el-dialog) {
  background: transparent;
  border-radius: 14px;
  box-shadow: none;
}

:deep(.appointment-modal .el-dialog__header) {
  padding: 0;
  margin: 0;
}

:deep(.appointment-modal .el-dialog__body) {
  padding: 0;
}

:deep(.appointment-modal .el-dialog__footer) {
  padding: 14px 0 0;
}

:deep(.appointment-modal .el-dialog__headerbtn .el-dialog__close) {
  color: #0c1f49;
  font-size: 24px;
}
</style>
